import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NewsCard from '@/components/feature/NewsCard';
import Pagination from '@/components/feature/Pagination';
import FilterPanel, { type SortOrder, type TimeRange } from './components/FilterPanel';
import { getCategoryById, getNewsByCategory, getReferenceNow, parsePublishedAt } from '@/utils/newsUtils';
import { useSeo } from '@/hooks/useSeo';

const PAGE_SIZE = 5;
const SPAN: Record<Exclude<TimeRange, 'all'>, number> = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
};

export default function Category() {
  const { categoryId } = useParams();
  const category = getCategoryById(categoryId);
  const [timeRange, setTimeRange] = useState<TimeRange>('all');
  const [sortOrder, setSortOrder] = useState<SortOrder>('time');
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useSeo({
    title: category ? `${category.name} - 吃瓜网热点新闻` : '分类未找到 - 吃瓜网',
    description: category
      ? `${category.description} 收录最近24小时、7天与30天的${category.name}热点内容，第一时间掌握最新动态。`
      : '该分类暂不存在，请返回吃瓜网首页浏览其他热点资讯。',
    keywords: category
      ? `${category.name},吃瓜网,热点新闻,${category.name}资讯,娱乐八卦`
      : '吃瓜网,热点新闻',
    canonicalPath: `/category/${categoryId}`,
  });

  const base = useMemo(() => (category ? getNewsByCategory(category.id) : []), [category]);

  const filtered = useMemo(() => {
    const now = getReferenceNow();
    const list = base.filter((n) =>
      timeRange === 'all' ? true : now - parsePublishedAt(n.publishedAt) <= SPAN[timeRange],
    );
    return [...list].sort((a, b) =>
      sortOrder === 'time'
        ? parsePublishedAt(b.publishedAt) - parsePublishedAt(a.publishedAt)
        : a.title.localeCompare(b.title, 'zh'),
    );
  }, [base, timeRange, sortOrder]);

  useEffect(() => {
    setPage(1);
  }, [timeRange, sortOrder, categoryId]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setTimeRange('all');
    setSortOrder('time');
    setPage(1);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 w-full">
        <div className="w-full px-4 md:px-8 py-6">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: category ? category.name : '分类未找到' }]} />

          {!category ? (
            <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
              <i className="ri-error-warning-line text-4xl text-foreground-300"></i>
              <p className="mt-4 text-base font-medium text-foreground-800">该分类不存在或已下线</p>
              <p className="mt-2 text-sm text-foreground-500">请返回首页浏览其他热点资讯</p>
              <Link
                to="/"
                className="mt-6 inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-primary-500 text-primary-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                返回首页
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          ) : (
            <>
              <header className="mt-5">
                <div className="flex items-center gap-3">
                  <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
                    <i className={`${category.icon} text-xl`}></i>
                  </span>
                  <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                    {category.name}
                  </h1>
                </div>
                <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
                  {category.description} 本频道按发布时间汇集最近24小时、7天与30天的热点内容，方便你按需筛选阅读。
                </p>
              </header>

              <div className="mt-6 lg:hidden">
                <button
                  type="button"
                  onClick={() => setMobileFilterOpen((v) => !v)}
                  className="w-full flex items-center justify-between h-11 px-4 rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-800 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-filter-3-line text-primary-500"></i>
                    筛选条件
                  </span>
                  <i className={`ri-arrow-down-s-line transition-transform ${mobileFilterOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileFilterOpen && (
                  <div className="mt-3 animate-fade-in">
                    <FilterPanel
                      timeRange={timeRange}
                      sortOrder={sortOrder}
                      onTimeChange={setTimeRange}
                      onSortChange={setSortOrder}
                      onReset={resetFilters}
                      resultCount={filtered.length}
                    />
                  </div>
                )}
              </div>

              <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
                <aside className="hidden lg:block lg:col-span-3">
                  <div className="sticky top-28">
                    <FilterPanel
                      timeRange={timeRange}
                      sortOrder={sortOrder}
                      onTimeChange={setTimeRange}
                      onSortChange={setSortOrder}
                      onReset={resetFilters}
                      resultCount={filtered.length}
                    />
                  </div>
                </aside>

                <div className="lg:col-span-9">
                  {paged.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
                      <i className="ri-search-eye-line text-4xl text-foreground-300"></i>
                      <p className="mt-4 text-base font-medium text-foreground-800">暂无符合条件的新闻</p>
                      <p className="mt-2 text-sm text-foreground-500">试试放宽时间范围，或重置筛选条件</p>
                      <button
                        type="button"
                        onClick={resetFilters}
                        className="mt-6 inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-primary-500 text-primary-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-refresh-line"></i>
                        重置筛选
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-4">
                      {paged.map((article) => (
                        <NewsCard key={article.id} article={article} layout="row" />
                      ))}
                    </div>
                  )}

                  {filtered.length > 0 && <Pagination current={page} total={totalPages} onChange={setPage} />}
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}